import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Leaf, Award } from "lucide-react";
import GlutenFreeIcon from "@/components/GlutenFreeIcon";
import PageSEO from "@/components/PageSEO";
import JsonLd from "@/components/JsonLd";
const AIC = () => {
  const navigate = useNavigate();
  const goToMenu = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('menu');
      if (element) {
        const headerHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <div className="min-h-screen bg-background">
      <PageSEO
        title="Pizza Senza Glutine Certificata AIC | Pizzeria La Torre"
        description="Pizza senza glutine certificata AIC. Impasto con farina di riso, grano saraceno e mais. Preparazione in ambienti sicuri senza contaminazione."
      />
      <JsonLd
        id="breadcrumb"
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.pizzerialatorrecarmagnola.it/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Pizza Senza Glutine AIC',
              item: 'https://www.pizzerialatorrecarmagnola.it/aic',
            },
          ],
        }}
      />
      <JsonLd
        id="aic"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Pizza Senza Glutine Certificata AIC',
          description:
            "Pizza senza glutine certificata AIC presso la Pizzeria La Torre di Carmagnola. Impasto a base di farina di riso, grano saraceno e mais, preparato in ambienti sicuri senza contaminazione.",
          mainEntityOfPage: 'https://www.pizzerialatorrecarmagnola.it/aic',
          author: { '@type': 'Organization', name: 'Pizzeria La Torre' },
          publisher: {
            '@type': 'Organization',
            name: 'Pizzeria La Torre',
            logo: {
              '@type': 'ImageObject',
              url: 'https://www.pizzerialatorrecarmagnola.it/android-chrome-512x512.png',
            },
          },
          about: { '@type': 'Thing', name: 'Celiachia / Senza Glutine' },
        }}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft size={20} />
            <span>Torna al sito</span>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
            <GlutenFreeIcon className="text-green-400" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Pizza Senza Glutine <span className="text-green-400">Certificata AIC</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Associazione Italiana Celiachia
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="card-elevated rounded-2xl p-8 md:p-12 mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              La nostra pizza senza glutine: gusto e sicurezza certificati AIC
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Siamo orgogliosi di far parte del programma <strong className="text-green-400">AIC – Associazione Italiana Celiachia</strong>, 
              una realtà riconosciuta a livello nazionale che tutela le persone celiache e garantisce 
              la massima sicurezza nella preparazione dei prodotti alimentari.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Essere parte dell'AIC significa rispettare protocolli rigorosi, utilizzare materie prime 
              controllate e lavorare in ambienti sicuri, per offrire a tutti il piacere di una pizza 
              buona, autentica e <strong className="text-foreground">senza rischi di contaminazione</strong>.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="card-elevated rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mb-4">
                <Shield className="text-primary" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Protocolli Rigorosi
              </h3>
              <p className="text-muted-foreground text-sm">
                Seguiamo procedure certificate per garantire la massima sicurezza
              </p>
            </div>

            <div className="card-elevated rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500/20 mb-4">
                <Leaf className="text-green-400" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Materie Prime Controllate
              </h3>
              <p className="text-muted-foreground text-sm">
                Ingredienti selezionati e certificati per la celiachia
              </p>
            </div>

            <div className="card-elevated rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/20 mb-4">
                <Award className="text-amber-400" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Ambienti Sicuri
              </h3>
              <p className="text-muted-foreground text-sm">
                Preparazione in aree dedicate senza contaminazione
              </p>
            </div>
          </div>

          {/* Impasto Section */}
          <div className="card-elevated rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Il nostro impasto speciale
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Il nostro impasto senza glutine nasce da una <strong className="text-foreground">miscela selezionata</strong> di:
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {['Farina di riso', 'Grano saraceno', 'Mais', 'Guar', 'Amido di mais'].map(ingredient => <span key={ingredient} className="px-4 py-2 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 text-sm font-medium">
                  {ingredient}
                </span>)}
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Una ricetta studiata per mantenere la <strong className="text-foreground">leggerezza e la fragranza</strong> tipica 
              della pizza tradizionale.
            </p>
          </div>

          {/* Result Quote */}
          <div className="mt-12 text-center">
            <blockquote className="text-2xl md:text-3xl text-foreground italic max-w-3xl mx-auto">
              "Una pizza soffice, gustosa e indistinguibile da quella classica, 
              perfetta per chi non vuole rinunciare al sapore della <span className="text-primary">vera pizza italiana</span>."
            </blockquote>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <button onClick={goToMenu} className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors">
              Scopri il nostro menù
            </button>
          </div>
        </div>
      </section>
    </div>;
};
export default AIC;